import React, { useMemo } from 'react';
import {
  ArrowTurnUpIcon,
  Box,
  Controller,
  Inscription,
  PriceInput,
  Text,
  useFormContext,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { TriggerCategory } from '@/modules/HealthPlan/HealthPlan.types';
import { RenewalDeductibleConfig } from '@/modules/Renewals/renewals.types';
import theme from '@/styles/theme';

import { EditAmountsFormValues } from '../EditAmountsModal.types';

import PlanFundingCard from './PlanFundingCard';

interface Props {
  configs: RenewalDeductibleConfig[];
  originalConfigs?: RenewalDeductibleConfig[];
  testId?: string;
}

const getTriggerLabel = (triggerName?: string, triggerCategoryType?: string): string => {
  if (triggerName) return triggerName;
  if (triggerCategoryType === TriggerCategory.EMBEDDED) return 'Individual amount';
  if (triggerCategoryType === TriggerCategory.FAMILY) return 'Family amount';
  return '-';
};

const CoverageLevelsSection: React.FC<Props> = ({
  configs,
  originalConfigs,
  testId = 'edit-amounts-modal',
}) => {
  const { control, trigger: triggerValidation, formState } = useFormContext<EditAmountsFormValues>();

  const cslsByConfigAndPlan = useMemo(
    () =>
      configs.map((config) => {
        const map = {} as Record<string, typeof config.categorySpendLimits>;
        (config.categorySpendLimits || []).forEach((csl) => {
          const key = _.toString(csl.planId ?? '');
          map[key] ??= [];
          map[key].push(csl);
        });
        return map;
      }),
    [configs]
  );

  if (!configs || configs.length === 0) return null;

  return (
    <Box background="module" round="moduleRound" pad="spacing24" gap="spacing4">
      <Inscription size="20px" color="textTitle" weight={700} lineHeight="28px">
        Coverage levels
      </Inscription>
      {configs.map((config, index) => {
        const hasTriggers = (config.triggerConfigs || []).length > 0;
        const hasPlans = (config.autoFundingConfigs || []).length > 0;

        return (
          <Box
            key={config.coverageId || index}
            data-testid={`${testId}_coverage-${config.coverageCode}`}
            gap="spacing12"
          >
            <Inscription size="large" color="textBody" weight={700} lineHeight="22px">
              {config.coverageName || ''}
            </Inscription>
            <Box>
              {/* Trigger amounts */}
              <Box
                pad="spacing16"
                border={{ size: 'small', color: 'border1' }}
                round="container2Round"
                background="canvas"
                gap="spacing2"
                margin={{ bottom: hasPlans ? 'spacing12' : '0px' }}
              >
                {hasTriggers && (
                  <Box
                    direction="row"
                    align="start"
                    wrap
                    style={{ columnGap: theme.spacings.spacing12, rowGap: theme.spacings.spacing12 }}
                  >
                    {(config.triggerConfigs || []).map((triggerMeta, triggerIndex) => (
                      <Box key={triggerIndex} width={`calc(50% - ${theme.spacings.spacing6})`}>
                        <Text color="textBody" margin={{ bottom: 'spacing8' }}>
                          {getTriggerLabel(triggerMeta.triggerName, triggerMeta.triggerCategoryType)}
                        </Text>
                        <Controller
                          name={`coverageLevels.${index}.triggers.${triggerIndex}.amount`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <PriceInput
                              testId={`${testId}_coverage-${config.coverageCode}_trigger-${triggerIndex}`}
                              name={field.name}
                              value={field.value}
                              onChange={(value: string) => {
                                field.onChange(value);
                                if (formState.isSubmitted) {
                                  triggerValidation();
                                }
                              }}
                              placeholder="Enter $ amount"
                              errorText={fieldState.error?.message}
                            />
                          )}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Plans sub-section */}
              {hasPlans && (
                <Box margin={{ left: 'spacing24' }}>
                  <Box direction="row" align="start" margin={{ bottom: 'spacing4' }}>
                    <ArrowTurnUpIcon
                      color="iconSecondary"
                      size="12px"
                      style={{ marginTop: theme.spacings.spacing2 }}
                    />
                    <Text size="small" color="textSecondary" weight={500} margin={{ left: 'spacing4' }}>
                      Plans
                    </Text>
                  </Box>
                  {(config.autoFundingConfigs || []).map((funding, planIndex) => (
                    <PlanFundingCard
                      key={funding.planId || planIndex}
                      funding={funding}
                      planIndex={planIndex}
                      coverageIndex={index}
                      planCsls={cslsByConfigAndPlan[index]?.[String(funding.planId ?? '')] || []}
                      originalConfig={originalConfigs?.[index]}
                      totalPlans={(config.autoFundingConfigs || []).length}
                      testId={testId}
                    />
                  ))}
                </Box>
              )}
              {index !== configs.length - 1 && (
                <Box height="1px" background="border1" margin={{ top: 'spacing12', bottom: 'spacing8' }} />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CoverageLevelsSection;
